import "pages/claims/AuditHistory.scss";

import Card from "components/Card/Card.tsx";
import { useParams } from "react-router-dom";
import { useGetFileAuditHistoryQuery } from "services/file-api";
import { useToken } from "utils/context.ts";
import { getTimeFromISOString } from "utils/date-utils";

import { nanoid } from "@reduxjs/toolkit";

type AuditHistoryDataType = {
  timestamp: string;
  action?: string;
  actor_username?: string;
  messages: string[];
};

const FileAuditHistory = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const { accessToken } = useToken();

  const {
    data: fileAuditHistory,
    isLoading,
    error,
  } = useGetFileAuditHistoryQuery({ id: fileId }, { skip: !accessToken });

  const auditData = (fileAuditHistory as AuditHistoryDataType[]) ?? [];

  const isNoHistoryError =
    error &&
    "status" in error &&
    error.status === 400 &&
    "data" in error &&
    (error.data as { detail?: string })?.detail ===
      "No history found for this file.";

  return (
    <Card heading="Audit History">
      {isLoading && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          Loading audit history...
        </div>
      )}

      {error && !isNoHistoryError && (
        <div style={{ padding: "20px", color: "red" }}>
          <strong>Error loading audit history:</strong>
          <pre style={{ marginTop: "10px", fontSize: "12px" }}>
            {JSON.stringify(error, null, 2)}
          </pre>
        </div>
      )}

      {!isLoading && (auditData.length === 0 || isNoHistoryError) && (
        <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
          No audit history available for this file.
        </div>
      )}

      {!isLoading &&
        !error &&
        !isNoHistoryError &&
        auditData.map((item) => {
          const formattedDate = getTimeFromISOString(
            item.timestamp,
            "dateTime",
          );
          return (
            <div key={nanoid()} className="AuditHistory">
              <label className="AuditHistory--date">{formattedDate}</label>
              <div className="AuditHistory--audit">
                <div className="AuditHistory--audit--header">
                  {item.action !== undefined && (
                    <>
                      File was {item.action}d{" - "}
                    </>
                  )}
                  Change by {item.actor_username || "unknown"}
                </div>
                {item.messages.map((message) => (
                  <div key={nanoid()} className="AuditHistory--audit--item">
                    {message}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
    </Card>
  );
};

export default FileAuditHistory;
